"use client"

import { useTranslations } from "next-intl"
import { CopyrightIcon, StarIcon } from "~/components/ui/icons"
import { cx, formatCompactNumber } from "~/lib/legacy-utils"

type RepositoryStats = {
  stars: number
  license?: string | null
  licenseExpression?: string | null
  licenseSource?: string
  licenseEvidenceUrl?: string | null
  requiresLicenseReview?: boolean
  revalidationStatus?: string | null
}

type ToolRepositoryStatsProps = {
  repository: RepositoryStats
  className?: string
}

const ToolRepositoryStats = ({ repository, className }: ToolRepositoryStatsProps) => {
  const t = useTranslations("tools.repository")
  const recordedLicense = repository.licenseExpression ?? repository.license
  const license =
    repository.licenseSource &&
    repository.licenseSource !== "Unknown" &&
    repository.licenseEvidenceUrl &&
    repository.revalidationStatus !== "ChangesRequireReview" &&
    recordedLicense !== "NOASSERTION"
      ? recordedLicense
      : null

  return (
    <ul
      aria-label="Repository summary"
      className={cx(
        "flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground",
        className,
      )}
    >
      <li title="GitHub stars" className="inline-flex items-center gap-1.5">
        <StarIcon className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="sr-only">{t("stars")}: </span>
        <span className="font-medium tabular-nums text-secondary-foreground">
          {formatCompactNumber(repository.stars)}
        </span>
      </li>
      {license && (
        <li title={`Code license: ${license}`} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true">·</span>
          <CopyrightIcon className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="sr-only">Code license: </span>
          <span className="font-medium text-secondary-foreground">{license}</span>
        </li>
      )}
    </ul>
  )
}

export { ToolRepositoryStats, type RepositoryStats }
